import React, { Component } from "react";
import ReactAudioPlayer from "react-audio-player";
import { withRouter, RouteComponentProps } from "react-router-dom";

import { AudioBible, filterBiblesByChapterId } from "api/typedefs/Bible";
import { isCordovaApp, CordovaAudio } from "../../../utils/cordovaUtils";
import { fetchAudioChapter } from "../../../api/BibleApi";
import { withChapter, ChapterCtx } from "../ChapterContainer";
import { withBible, BibleCtx } from "../../Bible/BibleContainer";

const ChapterAudioContext = React.createContext({});

interface ChapterAudioProps extends RouteComponentProps<{ bibleId: string }> {
  chapter: ChapterCtx;
  bible: BibleCtx;
}

export type AudioChapter = any;

export interface AudioCtx {
  position: number;
  duration: number;
  isPlayerEnabled: boolean; // internal state used to determine if player should fetch and buffer
  isLoading: boolean; // fetch of audio chapter happening or audio buffering initially
  audioChapters: {
    [chapterUid: string]: AudioChapter;
  };
  isPlayable: boolean; // an audio chapter is present
  isPlaying: boolean; // audio element is playing
  isPlayPending: boolean; // onPlayPause() has triggered a play, but audio element is not yet playing
  isControllerOpen: boolean;
  onToggleController: () => void;
  onHideController: () => void;
  onShowController: () => void;
  onSelectAudioBible: (audioBibleId: string) => void;
  onPlayPause: () => void;
  onAudioSeek: (position: number) => void;
  getCurrentAudioChapter: () => AudioChapter;
  selectedAudioBibleId: string | null;
  currentChapterUid: string | null;
  currentAudioVerseElement: HTMLSpanElement | null;
  currentAudioVerseId: string | null;
}

class ChapterAudio extends Component<ChapterAudioProps, AudioCtx> {
  cordovaAudio: CordovaAudio;
  reactAudioPlayer: any;

  static Consumer = ChapterAudioContext.Consumer;

  isComponentMounted = false;

  constructor(props: ChapterAudioProps) {
    super(props);
    this.cordovaAudio = new CordovaAudio();
    this.state = {
      position: 0,
      duration: 0,
      isPlayerEnabled: true,
      isLoading: false,
      audioChapters: {},
      isPlayable: false,
      isPlaying: false,
      isPlayPending: false,
      isControllerOpen: true,
      onToggleController: this.handleAudioControllerToggle,
      onHideController: this.handleHideAudioController,
      onShowController: this.handleShowAudioController,
      onSelectAudioBible: this.handleSelectAudioBible,
      onPlayPause: this.handlePlayPause,
      onAudioSeek: this.handleAudioSeek,
      getCurrentAudioChapter: this.getCurrentAudioChapter,
      selectedAudioBibleId: null,
      currentChapterUid: null,
      currentAudioVerseElement: null,
      currentAudioVerseId: null
    };
  }

  handleSelectAudioBible = (audioBibleId: string) => {
    this.setState({
      selectedAudioBibleId: audioBibleId
    });
  };

  componentDidMount() {
    const { bible, chapter: chapterCtx } = this.props;
    const { bibleId } = this.props.match.params;
    const currentBible = bible.biblesById[bibleId];
    this.isComponentMounted = true;
    if (isCordovaApp) this.cordovaAudio.cordovaStopAudio();
    const chapter = chapterCtx.getChapter();
    if (chapter && currentBible?.audioBibles) {
      const audioBibles = filterBiblesByChapterId(
        currentBible.audioBibles,
        chapter?.id
      );

      if (audioBibles.length) {
        const audioBibleId = audioBibles[0].id;
        const chapterId = chapter.id;

        this.loadAudioChapter({
          audioBibleId,
          chapterId
        });
      }
    }
  }

  shouldComponentUpdate = (nextProps: ChapterAudioProps) => {
    const nextChapter = nextProps.chapter.getChapter();
    return (
      nextProps.bible !== null &&
      nextChapter !== null &&
      nextChapter?.id !== null
    );
  };

  componentDidUpdate = () => {
    const { bible, chapter: chapterCtx } = this.props;
    const { bibleId } = this.props.match.params;
    const currentBible = bible.biblesById[bibleId];
    const {
      audioChapters,
      currentChapterUid,
      selectedAudioBibleId,
      isControllerOpen,
      isPlayable
    } = this.state;

    const chapter = chapterCtx.getChapter();
    if (chapter && currentBible?.audioBibles) {
      const audioBibles = filterBiblesByChapterId(
        currentBible.audioBibles,
        chapter?.id
      );

      if (audioBibles.length) {
        const audioBibleId = selectedAudioBibleId || audioBibles[0].id;
        const chapterId = chapter.id;
        const chapterUid = [audioBibleId, chapterId].join(":");
        const nextChapter = audioChapters[chapterUid];
        if (currentChapterUid !== chapterUid) {
          if (!isControllerOpen) {
            // disable player and reset state if controller was not open
            this.setState({
              isPlayerEnabled: false
            });
          }

          this.setState({
            duration: 0,
            position: 0,
            isPlaying: false,
            isLoading: true, // defaults to true when not in cordova
            // isPlayPending: isCordovaApp, // defaults to true when is in cordova
            currentChapterUid: chapterUid
          });

          if (nextChapter) {
            if (isCordovaApp) this.initCordovaPlayer(nextChapter.resourceUrl);

            this.setState({ isPlayable: true });
          } else {
            this.loadAudioChapter({
              audioBibleId,
              chapterId
            });
          }
        }
      }
    } else if (isPlayable) {
      this.setState({
        duration: 0,
        position: 0,
        isPlaying: false,
        isPlayerEnabled: true,
        isPlayable: false,
        currentChapterUid: null
      });
    }
  };

  componentWillUnmount = () => {
    this.isComponentMounted = false;
    if (isCordovaApp) this.cordovaAudio.cordovaStopAudio();
  };

  handlePlayPause = () => {
    if (!this.state.isPlaying) {
      this.play();
    } else {
      this.pause();
    }
  };

  handleAudioSeek = (position: number) => {
    if (isCordovaApp) {
      this.cordovaAudio.cordovaAudioSeekTo(position);
      this.updateState();
    } else {
      this.seekAudio(position);
    }
  };

  handleAudioControllerToggle = () => {
    if (!this.state.isControllerOpen) {
      return this.handleShowAudioController();
    }
    return this.handleHideAudioController();
  };

  handleHideAudioController = () => this.setState({ isControllerOpen: false });

  handleShowAudioController = () => {
    if (!this.state.isPlayerEnabled) {
      this.setState({ isPlayerEnabled: true });
    }
    this.setState({ isControllerOpen: true });
  };

  isAudioElementPlaying = () => {
    if (isCordovaApp) {
      return this.cordovaAudio.cordovaAudioIsPlaying();
    }
    return (
      this.reactAudioPlayer !== undefined &&
      this.reactAudioPlayer !== null &&
      !this.reactAudioPlayer.audioEl.paused &&
      !this.reactAudioPlayer.audioEl.ended
    );
  };

  updateState = (params?: {
    isPlayable?: boolean;
    isLoading?: boolean;
    isPlayPending?: boolean;
  }) => {
    const { isPlayable, isLoading, isPlayPending } = params || {};
    const newState = {
      duration: 0,
      position: 0,
      isPlaying: this.isAudioElementPlaying()
    } as any;

    if (isPlayable !== undefined) {
      newState.isPlayable = isPlayable;
    }

    if (isLoading !== undefined) {
      newState.isLoading = isLoading;
    }

    if (isPlayPending !== undefined) {
      newState.isPlayPending = isPlayPending;
    }

    if (isCordovaApp) {
      this.cordovaAudio
        .cordovaGetCurrentPosition()
        .then(updatedState => {
          const { verseId, verseIdElement } = this.getVerseFromAudioPosition(
            updatedState.position
          );
          this.setState({
            ...newState,
            ...updatedState,
            currentAudioVerseId: verseId,
            currentAudioVerseElement: verseIdElement
          });
        })
        .catch(err => {
          console.log("setting state in catch:", err);
          this.setState(newState);
        });
    } else {
      if (
        this.reactAudioPlayer !== undefined &&
        this.reactAudioPlayer !== null
      ) {
        newState.duration = this.reactAudioPlayer.audioEl.duration || 0;
        newState.position = this.reactAudioPlayer.audioEl.currentTime || 0;

        const { verseId, verseIdElement } = this.getVerseFromAudioPosition(
          newState.position
        );
        newState.currentAudioVerseId = verseId;
        newState.currentAudioVerseElement = verseIdElement;

        if (this.state.isPlayPending && newState.position !== 0) {
          newState.isPlayPending = false;
        }
      }
      this.setState(newState);
    }
  };

  getVerseFromAudioPosition(position: number) {
    let verseId;
    const currentAudioChapter = this.getCurrentAudioChapter();
    if (currentAudioChapter && currentAudioChapter.timecodes) {
      const { timecodes } = currentAudioChapter;
      const verseIdIndex = timecodes.findIndex(
        ({ end }: { end: number }) => position < end
      );
      if (verseIdIndex > -1) verseId = timecodes[verseIdIndex].verseId;
    }
    return {
      verseId,
      verseIdElement: document.querySelector(
        `.verse-span[data-verse-id="${verseId}"]`
      )
    };
  }

  loadAudioChapter({
    audioBibleId,
    chapterId
  }: {
    audioBibleId: string;
    chapterId: string;
  }) {
    return fetchAudioChapter({
      bibleId: audioBibleId,
      chapterId
    })
      .then(audioChapter => {
        if (audioChapter && this.isComponentMounted) {
          const chapterUid = [audioBibleId, audioChapter.id].join(":");
          if (isCordovaApp) this.initCordovaPlayer(audioChapter.resourceUrl);
          this.setState({
            audioChapters: {
              ...this.state.audioChapters,
              [chapterUid]: audioChapter
            },
            isPlayable: true
          });
        } else {
          throw new Error("No chapter returned from audio fetch!");
        }
        return audioChapter;
      })
      .catch(error => {
        console.log("Failed fetching audio chapter:", error);
        if (this.isComponentMounted) {
          this.updateState({
            isPlayable: false,
            isLoading: false
          });
        }
      });
  }

  handleMetadataLoad = () => {
    // audio chapter URL has loaded, but audio is still loading
    // until onCanPlay is fired by the react audio component
    if (this.isComponentMounted) {
      this.updateState();
    }
  };

  handlePlay = () => {
    this.updateState();
  };

  handleCanPlay = () => {
    // if (this.props.isPlaying) {
    //   this.reactAudioPlayer.audioEl.play();
    // }
    // this.props.onCanPlay();
    this.updateState({
      isLoading: false
    });
  };

  handleListen = () => {
    this.updateState();
  };

  handleEnded = () => {
    const { bible, chapter: chapterCtx, history } = this.props;
    const { bibleId } = this.props.match.params;
    const currentBible = bible.biblesById[bibleId];
    const chapter = chapterCtx.getChapter();
    const { isPlaying } = this.state;
    if (
      (chapter?.next && isPlaying) ||
      (isCordovaApp && this.cordovaAudio.cordovaAudioIsAutoPlay())
    ) {
      history.push(`/bible/${currentBible?.id}/${chapter?.next?.id}`);
    } else {
      if (isCordovaApp) this.cordovaAudio.cordovaStopAudio();
      else this.reactAudioPlayer.audioEl.currentTime = 0;
      this.updateState();
    }
  };

  play = () => {
    if (isCordovaApp) {
      this.cordovaAudio.cordovaPlayAudio();
      this.cordovaAudio.cordovaSetTimer(this.updateState, 200);
    } else {
      if (this.state.position === 0) {
        this.setState({ isPlayPending: true });
      }

      if (this.reactAudioPlayer) {
        // onPlay will trigger updateState()`
        this.reactAudioPlayer.audioEl.play();
      }
    }
  };

  pause = () => {
    if (this.isAudioElementPlaying()) {
      if (isCordovaApp) {
        this.cordovaAudio.cordovaPauseAudio();
      } else this.reactAudioPlayer.audioEl.pause();
      this.updateState();
    }
  };

  seekAudio = (position: number) => {
    if (this.reactAudioPlayer) {
      const audioElement = this.reactAudioPlayer.audioEl;
      // Is the player currently seeking?
      const isSeeking = audioElement.seeking;

      // Is the media seekable?
      const isSeekable =
        audioElement.seekable && audioElement.seekable.length > 0;

      if (!isSeeking && isSeekable) {
        audioElement.currentTime = position;
      }

      this.updateState();
    }
  };

  getCurrentAudioChapter = () => {
    const { chapter: chapterCtx, bible } = this.props;
    const { bibleId } = this.props.match.params;
    const currentBible = bible.biblesById[bibleId];
    const { audioChapters, selectedAudioBibleId } = this.state;

    const chapter = chapterCtx.getChapter();
    if (chapter && currentBible?.audioBibles) {
      const audioBibles = filterBiblesByChapterId(
        currentBible.audioBibles,
        chapter?.id
      );
      if (audioBibles.length) {
        const audioBibleId = selectedAudioBibleId || audioBibles[0].id;
        const chapterId = chapter.id;
        const chapterUid = [audioBibleId, chapterId].join(":");
        const currentAudioChapter = audioChapters[chapterUid];
        return currentAudioChapter;
      }
    }
    return null;
  };

  initCordovaPlayer = (src?: string) => {
    const { chapter: chapterCtx, bible } = this.props;
    const { bibleId } = this.props.match.params;
    const currentBible = bible.biblesById[bibleId];
    const chapter = chapterCtx.getChapter();
    this.cordovaAudio.cordovaPrepAudio(
      src || "",
      this.updateState,
      this.handleEnded,
      {
        track: chapter?.reference || "reference",
        artist: currentBible?.nameLocal || "nameLocal"
      }
    );
    if (this.cordovaAudio.cordovaAudioIsAutoPlay()) {
      this.handlePlayPause();
    }
  };

  render() {
    const { bible, chapter: chapterCtx, children } = this.props;
    const {
      audioChapters,
      isPlayerEnabled,
      isPlayable,
      selectedAudioBibleId
    } = this.state;

    const chapter = chapterCtx.getChapter();
    const { bibleId } = this.props.match.params;
    const currentBible = bible.biblesById[bibleId];

    let audioBibles: AudioBible[] = [];
    if (chapter && currentBible?.audioBibles) {
      audioBibles = filterBiblesByChapterId(
        currentBible.audioBibles,
        chapter?.id
      );
    }

    const audioBibleId = selectedAudioBibleId || audioBibles[0]?.id || "";
    const chapterId = chapter?.id || "";
    const chapterUid = [audioBibleId, chapterId].join(":");
    const currentAudioChapter = audioChapters[chapterUid];
    const audioIsNotExpired =
      currentAudioChapter?.expiresAt < new Date().getTime();

    const showWebPlayer =
      chapter &&
      currentBible &&
      Boolean(audioBibles.length) &&
      currentAudioChapter &&
      isPlayerEnabled &&
      isPlayable &&
      audioIsNotExpired;

    return (
      <ChapterAudioContext.Provider value={this.state}>
        {children}
        {!isCordovaApp && showWebPlayer && (
          <ReactAudioPlayer
            prefetch="metadata"
            src={currentAudioChapter.resourceUrl || ""}
            onLoadedMetadata={this.handleMetadataLoad}
            onCanPlay={this.handleCanPlay}
            onPlay={this.handlePlay}
            onListen={this.handleListen}
            onEnded={this.handleEnded}
            listenInterval={500}
            ref={(element: any) => {
              this.reactAudioPlayer = element;
            }}
          />
        )}
      </ChapterAudioContext.Provider>
    );
  }
}

export default withRouter(withBible(withChapter(ChapterAudio)));

interface WithAudioProps {
  audio: AudioCtx;
}

export function withAudio<Props extends WithAudioProps>(
  WrappedComponent: React.ComponentType<Props>
) {
  return function ComponentWithAudio(props: any) {
    return (
      <ChapterAudio.Consumer>
        {context => <WrappedComponent {...props} audio={context} />}
      </ChapterAudio.Consumer>
    );
  };
}
